import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types
export interface Ad {
  id: number;
  name: string;
  dateStart: string;
  dateEnd: string;
  budget?: number | null;
  platform: 'instagram' | 'telegram';
  channelType: 'native' | 'partnership';
  description?: string | null;
  goal: 'ig_followers' | 'tg_followers' | 'sales';
  consultationCount?: number;
  paidCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdsState {
  ads: Ad[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
  };
}

// Initial state
const initialState: AdsState = {
  ads: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
  },
};

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchAdsAsync = createAsyncThunk(
  'ads/fetchAds',
  async (params: { page?: number; limit?: number; search?: string } = {}, { rejectWithValue, getState }) => {
    try {
      const { page = 1, limit = 50, search = '' } = params;
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) {
        queryParams.append('search', search);
      }

      const response = await axios.get(
        `${API_BASE_URL}/ads?${queryParams.toString()}`,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch ads'));
    }
  }
);

export const createAdAsync = createAsyncThunk(
  'ads/createAd',
  async (adData: Partial<Ad>, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/ads`,
        adData,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create ad'));
    }
  }
);

export const updateAdAsync = createAsyncThunk(
  'ads/updateAd',
  async ({ id, data }: { id: number; data: Partial<Ad> }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/ads/${id}`,
        data,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update ad'));
    }
  }
);

export const deleteAdAsync = createAsyncThunk(
  'ads/deleteAd',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/ads/${id}`,
        { headers: getAuthHeaders(getState) }
      );

      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete ad'));
    }
  }
);

// Slice
const adsSlice = createSlice({
  name: 'ads',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<{ search?: string }>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
      };
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch ads
      .addCase(fetchAdsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.ads = action.payload.ads;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAdsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create ad
      .addCase(createAdAsync.fulfilled, (state, action) => {
        state.ads.unshift(action.payload.ad);
      })
      // Update ad
      .addCase(updateAdAsync.fulfilled, (state, action) => {
        const index = state.ads.findIndex(a => a.id === action.payload.ad.id);
        if (index !== -1) {
          state.ads[index] = action.payload.ad;
        }
      })
      // Delete ad
      .addCase(deleteAdAsync.fulfilled, (state, action) => {
        state.ads = state.ads.filter(a => a.id !== action.payload);
      });
  },
});

export const { clearError, setFilters, clearFilters, setPage } = adsSlice.actions;
export default adsSlice.reducer;
